/**
 * features/members/api.ts — typed wrappers around /api/tenant/members and
 * /api/tenant/staff-profiles. Every tenant-scoped call includes X-Tenant-Id
 * since the backend requires it (requireTenantContext middleware).
 */

import { z } from 'zod';
import { api } from '@/lib/api';
import {
  ArchiveResponseSchema,
  CatalogListResponseSchema,
  ChatCancelResponseSchema,
  ChatClearResponseSchema,
  ChatListResponseSchema,
  ChatSendResponseSchema,
  EnhanceResponseSchema,
  AddMemberResponseSchema,
  MemberDetailResponseSchema,
  ListMemberMemoriesResponseSchema,
  MembersListResponseSchema,
  RecommendedJobsResponseSchema,
  RecordMemoryResponseSchema,
  RetrievedResourcesResponseSchema,
  StaffProfileDetailResponseSchema,
  StaffProfilesListResponseSchema,
  UpdateResponseSchema,
  type AddHumanBody,
  type ArchiveResponse,
  type CatalogListResponse,
  type ChatCancelResponse,
  type ChatClearResponse,
  type ChatListResponse,
  type ChatSendResponse,
  type ClearChatOptions,
  type EnhanceKind,
  type EnhanceResponse,
  type AddMemberRequest,
  type AddMemberResponse,
  type MemberDetailResponse,
  type ListMemberMemoriesResponse,
  type MembersListResponse,
  type RecommendedJobsResponse,
  type RecordMemoryResponse,
  type RetrievedResourcesResponse,
  type StaffProfileDetailResponse,
  type StaffProfilesListResponse,
  type UpdateMemberRequest,
  type UpdateResponse,
  MatchResponseSchema,
  type MatchRequest,
  type MatchResponse,
} from './schemas';

function tenantHeaders(tenantId: string): Record<string, string> {
  return { 'X-Tenant-Id': tenantId };
}

export async function listMembers(tenantId: string): Promise<MembersListResponse> {
  return api.get('/tenant/members', MembersListResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function getMember(tenantId: string, memberId: string): Promise<MemberDetailResponse> {
  return api.get(`/tenant/members/${encodeURIComponent(memberId)}`, MemberDetailResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function getRecommendedJobs(
  tenantId: string,
  memberId: string,
): Promise<RecommendedJobsResponse> {
  return api.get(
    `/tenant/members/${encodeURIComponent(memberId)}/recommended-jobs`,
    RecommendedJobsResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function listStaffProfiles(tenantId: string): Promise<StaffProfilesListResponse> {
  return api.get('/tenant/staff-profiles', StaffProfilesListResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function getStaffProfileDetail(
  tenantId: string,
  id: string,
): Promise<StaffProfileDetailResponse> {
  return api.get(
    `/tenant/staff-profiles/${encodeURIComponent(id)}`,
    StaffProfileDetailResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function addMember(
  tenantId: string,
  values: AddMemberRequest,
): Promise<AddMemberResponse> {
  // RHF + zod will have already dropped empty-string fields via the schema's
  // .transform; but defend at the boundary in case a caller passes raw values.
  const body = Object.fromEntries(
    Object.entries(values).filter(([, v]) => v !== undefined && v !== ''),
  );
  return api.post('/tenant/members', AddMemberResponseSchema, body, {
    headers: tenantHeaders(tenantId),
  });
}

export async function updateMember(
  tenantId: string,
  memberId: string,
  patch: UpdateMemberRequest,
): Promise<UpdateResponse> {
  return api.patch(`/tenant/members/${encodeURIComponent(memberId)}`, UpdateResponseSchema, patch, {
    headers: tenantHeaders(tenantId),
  });
}

export async function archiveMember(tenantId: string, memberId: string): Promise<ArchiveResponse> {
  return api.post(
    `/tenant/members/${encodeURIComponent(memberId)}/archive`,
    ArchiveResponseSchema,
    undefined,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function listPositionsCatalog(tenantId: string): Promise<CatalogListResponse> {
  return api.get('/tenant/positions', CatalogListResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function listUnitsCatalog(tenantId: string): Promise<CatalogListResponse> {
  return api.get('/tenant/units', CatalogListResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function enhance(
  tenantId: string,
  args: { kind: EnhanceKind; content: string; userRequest?: string },
): Promise<EnhanceResponse> {
  // Trim + omit when blank — the backend defaults to "Make this better."
  // when the field is missing. Sending nothing is the cleanest signal.
  const trimmed = args.userRequest?.trim();
  const body: { kind: EnhanceKind; content: string; userRequest?: string } = {
    kind: args.kind,
    content: args.content,
  };
  if (trimmed) body.userRequest = trimmed;
  return api.post('/tenant/llm/enhance', EnhanceResponseSchema, body, {
    headers: tenantHeaders(tenantId),
  });
}

export async function sendChatMessage(
  tenantId: string,
  args: { memberId: string; content: string; clientMessageId?: string; attachment_ids?: string[] },
): Promise<ChatSendResponse> {
  return api.post('/tenant/chat/messages', ChatSendResponseSchema, args, {
    headers: tenantHeaders(tenantId),
  });
}

export async function listChatMessages(
  tenantId: string,
  args: { memberId: string; beforeAt?: string; beforeId?: string; limit?: number },
): Promise<ChatListResponse> {
  const params = new URLSearchParams({ memberId: args.memberId });
  if (args.beforeAt) params.set('beforeAt', args.beforeAt);
  if (args.beforeId) params.set('beforeId', args.beforeId);
  if (args.limit) params.set('limit', String(args.limit));
  return api.get(`/tenant/chat/messages?${params}`, ChatListResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function cancelChatMessage(
  tenantId: string,
  messageId: string,
): Promise<ChatCancelResponse> {
  return api.post(
    `/tenant/chat/messages/${encodeURIComponent(messageId)}/cancel`,
    ChatCancelResponseSchema,
    undefined,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function clearChatThread(
  tenantId: string,
  memberId: string,
  options: ClearChatOptions,
): Promise<ChatClearResponse> {
  return api.post(
    '/tenant/chat/clear',
    ChatClearResponseSchema,
    { memberId, ...options },
    { headers: tenantHeaders(tenantId) },
  );
}

export async function recordMemory(
  tenantId: string,
  memberId: string,
  body: { content: string; source: { kind: 'chat_pin'; id: string } },
): Promise<RecordMemoryResponse> {
  return api.post(
    `/tenant/members/${encodeURIComponent(memberId)}/memories`,
    RecordMemoryResponseSchema,
    body,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function listMemberMemories(
  tenantId: string,
  memberId: string,
): Promise<ListMemberMemoriesResponse> {
  return api.get(
    `/tenant/members/${encodeURIComponent(memberId)}/memories`,
    ListMemberMemoriesResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function listRetrievedResources(
  tenantId: string,
  chatMessageId: string,
): Promise<RetrievedResourcesResponse> {
  return api.get(
    `/tenant/chat/messages/${encodeURIComponent(chatMessageId)}/retrieved-resources`,
    RetrievedResourcesResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function matchStaffProfiles(
  tenantId: string,
  body: MatchRequest,
): Promise<MatchResponse> {
  return api.post('/tenant/staff-profiles/match', MatchResponseSchema, body, {
    headers: tenantHeaders(tenantId),
  });
}

/** EventSource can't set headers; tenant id rides as _t query param (spec §5.3). */
export function chatStreamUrl(tenantId: string, memberId: string): string {
  const params = new URLSearchParams({
    memberId,
    _t: tenantId,
  });
  return `/api/tenant/chat/stream?${params}`;
}

// ── Member Attachments ───────────────────────────────────────────────────────

const MemberAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  mime_type: z.string(),
  size_bytes: z.number(),
  sha256: z.string().optional(),
  created_at: z.string(),
});

const MemberAttachmentsListResponseSchema = z.object({
  items: z.array(MemberAttachmentSchema),
});

const MemberAttachmentResponseSchema = z.object({
  attachment: MemberAttachmentSchema,
});

export type MemberAttachment = z.infer<typeof MemberAttachmentSchema>;

export async function listMemberAttachments(tenantId: string, memberId: string) {
  return api.get(
    `/tenant/members/${encodeURIComponent(memberId)}/attachments`,
    MemberAttachmentsListResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function linkAttachmentToMember(
  tenantId: string,
  memberId: string,
  attachmentId: string,
) {
  return api.post(
    `/tenant/members/${encodeURIComponent(memberId)}/attachments`,
    MemberAttachmentResponseSchema,
    { attachment_id: attachmentId },
    { headers: tenantHeaders(tenantId) },
  );
}

export async function unlinkAttachmentFromMember(
  tenantId: string,
  memberId: string,
  attachmentId: string,
) {
  return api.delete(
    `/tenant/members/${encodeURIComponent(memberId)}/attachments/${encodeURIComponent(attachmentId)}`,
    z.void(),
    { headers: tenantHeaders(tenantId) },
  );
}

export async function addHuman(tenantId: string, body: AddHumanBody): Promise<AddMemberResponse> {
  return api.post('/tenant/members/add-human', AddMemberResponseSchema, body, {
    headers: tenantHeaders(tenantId),
  });
}
